import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { get, post, put, del, patch } from '../../api/base';
import { MatrixType, MatrixSeverityType, MatrixLikelihoodType, MatrixRiskType } from '../../types/types';

interface MatrixSliceState {
  matrix: MatrixType;
  matrices: MatrixType[];
  severityLevels: MatrixSeverityType[];
  likelihoodLevels: MatrixLikelihoodType[];
  riskLevels: MatrixRiskType[];
  loading: boolean;
  error: any;
}

export const initialState: MatrixSliceState = {
  matrix: {
    id: null,
    name: '',
    project: {
      id: null,
      name: '',
      company: null,
      created_by: null,
      customer: '',
      business_unit: '',
      facility: '',
      description: '',
    },
    created_at: '',
    created_by: null,
    updated_at: '',
    updated_by: null,
    inverse: false,
  },
  matrices: [],
  severityLevels: [],
  likelihoodLevels: [],
  riskLevels: [],
  loading: false,
  error: null,
};

const normalizeRelationId = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const resolveMatrixIdFromRisk = (riskLevel: MatrixRiskType): number | null => {
  const candidates = [
    normalizeRelationId(
      (riskLevel as any)?.matrix?.id ?? (riskLevel as any)?.matrix_id ?? (riskLevel as any)?.matrix
    ),
    normalizeRelationId(
      (riskLevel as any)?.severity?.matrix?.id ??
        (riskLevel as any)?.severity?.matrix_id ??
        (riskLevel as any)?.severity?.matrix
    ),
    normalizeRelationId(
      (riskLevel as any)?.likelihood?.matrix?.id ??
        (riskLevel as any)?.likelihood?.matrix_id ??
        (riskLevel as any)?.likelihood?.matrix
    ),
  ];
  return candidates.find((id) => id !== null) ?? null;
};

const resolveMatrixIdFromLevel = (
  level: MatrixSeverityType | MatrixLikelihoodType | null | undefined
): number | null => {
  if (!level) {
    return null;
  }
  const candidate = (level as any)?.matrix?.id ?? (level as any)?.matrix_id ?? (level as any)?.matrix;
  return normalizeRelationId(candidate);
};

export const getAllMatricesByProjectId = createAsyncThunk(
  'matrices/getAllMatricesByProjectId',
  async (projectId: number, { rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const headers = {
        Authorization: `Bearer ${state.user.access}`,
      };
      const response = await get(`project/${projectId}/matrices/`, {
        headers: headers,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export type getMatrixByIdType = {
  project_id: number;
  matrix_id: number;
};

export const getMatrix = createAsyncThunk(
  'matrices/getMatrix',
  async ({ project_id, matrix_id }: getMatrixByIdType, { rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const headers = {
        Authorization: `Bearer ${state.user.access}`,
      };
      const response = await get(`project/${project_id}/matrices/${matrix_id}/`, {
        headers: headers,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export type CloneMatrixPayload = {
  projectId: number;
  matrixId: number;
};

export const cloneMatrix = createAsyncThunk(
  'matrices/cloneMatrix',
  async ({ projectId, matrixId }: CloneMatrixPayload, { rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const headers = {
        Authorization: `Bearer ${state.user.access}`,
      };
      const response = await post(`project/${projectId}/matrices/${matrixId}/clone/`, {}, { headers });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data ?? error);
    }
  }
);

export const createMatrix = createAsyncThunk(
  'matrices/createMatrix',
  async (matrix: MatrixType, { rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const headers = {
        Authorization: `Bearer ${state.user.access}`,
      };
      const response = await post(`project/${matrix.project.id}/matrices/`, matrix, {
        headers: headers,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateMatrix = createAsyncThunk(
  'matrices/updateMatrix',
  async (matrix: MatrixType, { rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const headers = {
        Authorization: `Bearer ${state.user.access}`,
      };
      const response = await put(`project/${matrix.project.id}/matrices/${matrix.id}/`, matrix, {
        headers: headers,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export type UpdateMatrixInversePayload = {
  projectId: number;
  matrixId: number;
  inverse: boolean;
};

export const updateMatrixInverse = createAsyncThunk(
  'matrices/updateMatrixInverse',
  async ({ projectId, matrixId, inverse }: UpdateMatrixInversePayload, { rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const headers = {
        Authorization: `Bearer ${state.user.access}`,
      };
      const response = await patch(`project/${projectId}/matrices/${matrixId}/inverse/`, { inverse }, { headers });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data ?? error);
    }
  }
);

export const deleteMatrix = createAsyncThunk(
  'matrices/deleteMatrix',
  async (matrix: Partial<MatrixType>, { rejectWithValue, getState }) => {
    if (!matrix || !matrix.id || isNaN(matrix.id)) {
      return rejectWithValue('Invalid matrix ID');
    }
    if (!matrix.project || !matrix.project.id || isNaN(matrix.project.id)) {
      return rejectWithValue('Invalid project ID');
    }
    try {
      const state: any = getState();
      const headers = {
        Authorization: `Bearer ${state.user.access}`,
      };
      const response = await del(`project/${matrix.project.id}/matrices/${matrix.id}/`, {
        headers: headers,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getSeverityLevelsByMatrixId = createAsyncThunk(
  'matrices/getSeverityLevelsByMatrixId',
  async (matrixId: number, { rejectWithValue, getState }) => {
    try {
      if (!matrixId || Number.isNaN(matrixId)) {
        return rejectWithValue('Invalid matrix ID');
      }
      const state: any = getState();
      const headers = {
        Authorization: `Bearer ${state.user.access}`,
      };
      const response = await get(`matrix/${matrixId}/severity_levels/`, {
        headers: headers,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getLikelihoodLevelsByMatrixId = createAsyncThunk(
  'matrices/getLikelihoodLevelsByMatrixId',
  async (matrixId: number, { rejectWithValue, getState }) => {
    try {
      if (!matrixId || Number.isNaN(matrixId)) {
        return rejectWithValue('Invalid matrix ID');
      }
      const state: any = getState();
      const headers = {
        Authorization: `Bearer ${state.user.access}`,
      };
      const response = await get(`matrix/${matrixId}/likelihood_levels/`, {
        headers: headers,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getRiskLevelsByMatrixId = createAsyncThunk(
  'matrices/getRiskLevelsByMatrixId',
  async (matrixId: number, { rejectWithValue, getState }) => {
    try {
      if (!matrixId || Number.isNaN(matrixId)) {
        return rejectWithValue('Invalid matrix ID');
      }
      const state: any = getState();
      const headers = {
        Authorization: `Bearer ${state.user.access}`,
      };
      const response = await get(`matrix/${matrixId}/risk_levels/`, {
        headers: headers,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const createSeverityLevel = createAsyncThunk(
  'matrices/createSeverityLevel',
  async (severityLevel: MatrixSeverityType, { rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const headers = {
        Authorization: `Bearer ${state.user.access}`,
      };
      const matrixId = resolveMatrixIdFromLevel(severityLevel);
      if (!matrixId) {
        return rejectWithValue('Invalid matrix ID');
      }
      const payload = {
        ...severityLevel,
        matrix: { ...(severityLevel.matrix ?? {}), id: matrixId },
      };
      const response = await post(`matrix/${matrixId}/severity_levels/`, payload, {
        headers: headers,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const createLikelihoodLevel = createAsyncThunk(
  'matrices/createLikelihoodLevel',
  async (likelihoodLevel: MatrixLikelihoodType, { rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const headers = {
        Authorization: `Bearer ${state.user.access}`,
      };
      const matrixId = resolveMatrixIdFromLevel(likelihoodLevel);
      if (!matrixId) {
        return rejectWithValue('Invalid matrix ID');
      }
      const payload = {
        ...likelihoodLevel,
        matrix: { ...(likelihoodLevel.matrix ?? {}), id: matrixId },
      };
      const response = await post(`matrix/${matrixId}/likelihood_levels/`, payload, {
        headers: headers,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const createRiskLevel = createAsyncThunk(
  'matrices/createRiskLevel',
  async (riskLevel: MatrixRiskType, { rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const headers = {
        Authorization: `Bearer ${state.user.access}`,
      };
      const matrixId = resolveMatrixIdFromRisk(riskLevel);
      const severityId = normalizeRelationId((riskLevel as any)?.severity?.id ?? riskLevel.severity_id);
      const likelihoodId = normalizeRelationId((riskLevel as any)?.likelihood?.id ?? riskLevel.likelihood_id);

      if (!matrixId || !severityId || !likelihoodId) {
        return rejectWithValue('Invalid matrix, severity or likelihood ID');
      }

      const newRiskLevel = {
        ...riskLevel,
        matrix: { ...(riskLevel.matrix ?? {}), id: matrixId },
        severity: severityId,
        likelihood: likelihoodId,
      };
      const response = await post(`matrix/${matrixId}/risk_levels/`, newRiskLevel, {
        headers: headers,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateSeverityLevel = createAsyncThunk(
  'matrices/updateSeverityLevel',
  async (severityLevel: MatrixSeverityType, { rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const headers = {
        Authorization: `Bearer ${state.user.access}`,
      };
      const matrixId = resolveMatrixIdFromLevel(severityLevel);
      if (!matrixId) {
        return rejectWithValue('Invalid matrix ID');
      }
      const response = await put(
        `matrix/${matrixId}/severity_levels/${severityLevel.id}/`,
        { ...severityLevel, matrix: { ...(severityLevel.matrix ?? {}), id: matrixId } },
        {
          headers: headers,
        }
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateLikelihoodLevel = createAsyncThunk(
  'matrices/updateLikelihoodLevel',
  async (likelihoodLevel: MatrixLikelihoodType, { rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const headers = {
        Authorization: `Bearer ${state.user.access}`,
      };
      const matrixId = resolveMatrixIdFromLevel(likelihoodLevel);
      if (!matrixId) {
        return rejectWithValue('Invalid matrix ID');
      }
      const response = await put(
        `matrix/${matrixId}/likelihood_levels/${likelihoodLevel.id}/`,
        { ...likelihoodLevel, matrix: { ...(likelihoodLevel.matrix ?? {}), id: matrixId } },
        {
          headers: headers,
        }
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateRiskLevel = createAsyncThunk(
  'matrices/updateRiskLevel',
  async (riskLevel: MatrixRiskType, { rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const headers = {
        Authorization: `Bearer ${state.user.access}`,
      };
      const matrixId = resolveMatrixIdFromRisk(riskLevel);
      const severityId = normalizeRelationId((riskLevel as any)?.severity?.id ?? riskLevel.severity_id);
      const likelihoodId = normalizeRelationId((riskLevel as any)?.likelihood?.id ?? riskLevel.likelihood_id);

      if (!matrixId || !severityId || !likelihoodId || !riskLevel.id) {
        return rejectWithValue('Invalid matrix, severity, likelihood or risk level ID');
      }

      const newRiskLevel = {
        ...riskLevel,
        matrix: { ...(riskLevel.matrix ?? {}), id: matrixId },
        severity: severityId,
        likelihood: likelihoodId,
      };
      const response = await put(`matrix/${matrixId}/risk_levels/${riskLevel.id}/`, newRiskLevel, {
        headers: headers,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteSeverityLevel = createAsyncThunk(
  'matrices/deleteSeverityLevel',
  async (severityLevel: MatrixSeverityType, { rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const headers = {
        Authorization: `Bearer ${state.user.access}`,
      };
      const matrixId = resolveMatrixIdFromLevel(severityLevel);
      if (!matrixId) {
        return rejectWithValue('Invalid matrix ID');
      }
      const response = await del(`matrix/${matrixId}/severity_levels/${severityLevel.id}/`, {
        headers: headers,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteLikelihoodLevel = createAsyncThunk(
  'matrices/deleteLikelihoodLevel',
  async (likelihoodLevel: MatrixLikelihoodType, { rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const headers = {
        Authorization: `Bearer ${state.user.access}`,
      };
      const matrixId = resolveMatrixIdFromLevel(likelihoodLevel);
      if (!matrixId) {
        return rejectWithValue('Invalid matrix ID');
      }
      const response = await del(`matrix/${matrixId}/likelihood_levels/${likelihoodLevel.id}/`, {
        headers: headers,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteRiskLevel = createAsyncThunk(
  'matrices/deleteRiskLevel',
  async (riskLevel: MatrixRiskType, { rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const headers = {
        Authorization: `Bearer ${state.user.access}`,
      };
      const matrixId = resolveMatrixIdFromRisk(riskLevel);
      if (!matrixId || !riskLevel.id) {
        return rejectWithValue('Invalid matrix or risk level ID');
      }
      const response = await del(`matrix/${matrixId}/risk_levels/${riskLevel.id}/`, {
        headers: headers,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const matrixSlice = createSlice({
  name: 'matrices',
  initialState,
  reducers: {
    setMatrix: (state, action) => {
      state.matrix = action.payload;
    },
    setMatrices: (state, action) => {
      state.matrices = action.payload;
    },
    setSeverityLevels: (state, action) => {
      state.severityLevels = action.payload;
    },
    setLikelihoodLevels: (state, action) => {
      state.likelihoodLevels = action.payload;
    },
    setRiskLevels: (state, action) => {
      state.riskLevels = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllMatricesByProjectId.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllMatricesByProjectId.fulfilled, (state, action) => {
        state.loading = false;
        state.matrices = action.payload;
      })
      .addCase(getAllMatricesByProjectId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getMatrix.pending, (state) => {
        state.loading = true;
      })
      .addCase(getMatrix.fulfilled, (state, action) => {
        state.loading = false;
        state.matrix = action.payload;
      })
      .addCase(getMatrix.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(cloneMatrix.pending, (state) => {
        state.loading = true;
      })
      .addCase(cloneMatrix.fulfilled, (state, action) => {
        state.loading = false;
        state.matrix = action.payload;
        state.matrices = [...state.matrices, action.payload];
      })
      .addCase(cloneMatrix.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createMatrix.pending, (state) => {
        state.loading = true;
      })
      .addCase(createMatrix.fulfilled, (state, action) => {
        state.loading = false;
        state.matrix = action.payload;
      })
      .addCase(createMatrix.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateMatrix.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateMatrix.fulfilled, (state, action) => {
        state.loading = false;
        state.matrix = action.payload;
      })
      .addCase(updateMatrix.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateMatrixInverse.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateMatrixInverse.fulfilled, (state, action) => {
        state.loading = false;
        state.matrix = { ...state.matrix, ...action.payload };
      })
      .addCase(updateMatrixInverse.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteMatrix.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteMatrix.fulfilled, (state) => {
        state.loading = false;
        state.matrix = initialState.matrix;
      })
      .addCase(deleteMatrix.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getSeverityLevelsByMatrixId.pending, (state) => {
        state.loading = true;
      })
      .addCase(getSeverityLevelsByMatrixId.fulfilled, (state, action) => {
        state.loading = false;
        state.severityLevels = action.payload;
      })
      .addCase(getSeverityLevelsByMatrixId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getLikelihoodLevelsByMatrixId.pending, (state) => {
        state.loading = true;
      })
      .addCase(getLikelihoodLevelsByMatrixId.fulfilled, (state, action) => {
        state.loading = false;
        state.likelihoodLevels = action.payload;
      })
      .addCase(getLikelihoodLevelsByMatrixId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getRiskLevelsByMatrixId.pending, (state) => {
        state.loading = true;
      })
      .addCase(getRiskLevelsByMatrixId.fulfilled, (state, action) => {
        state.loading = false;
        state.riskLevels = action.payload;
      })
      .addCase(getRiskLevelsByMatrixId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createSeverityLevel.pending, (state) => {
        state.loading = true;
      })
      .addCase(createSeverityLevel.fulfilled, (state, action) => {
        state.loading = false;
        state.severityLevels.push(action.payload);
      })
      .addCase(createSeverityLevel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createLikelihoodLevel.pending, (state) => {
        state.loading = true;
      })
      .addCase(createLikelihoodLevel.fulfilled, (state, action) => {
        state.loading = false;
        state.likelihoodLevels.push(action.payload);
      })
      .addCase(createLikelihoodLevel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createRiskLevel.pending, (state) => {
        state.loading = true;
      })
      .addCase(createRiskLevel.fulfilled, (state, action) => {
        state.loading = false;
        state.riskLevels.push(action.payload);
      })
      .addCase(createRiskLevel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateSeverityLevel.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateSeverityLevel.fulfilled, (state, action) => {
        state.loading = false;
        state.severityLevels = state.severityLevels.map((severityLevel) =>
          severityLevel.id === action.payload.id ? action.payload : severityLevel
        );
      })
      .addCase(updateSeverityLevel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateLikelihoodLevel.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateLikelihoodLevel.fulfilled, (state, action) => {
        state.loading = false;
        state.likelihoodLevels = state.likelihoodLevels.map((likelihoodLevel) =>
          likelihoodLevel.id === action.payload.id ? action.payload : likelihoodLevel
        );
      })
      .addCase(updateLikelihoodLevel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateRiskLevel.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateRiskLevel.fulfilled, (state, action) => {
        state.loading = false;
        state.riskLevels = state.riskLevels.map((riskLevel) =>
          riskLevel.id === action.payload.id ? action.payload : riskLevel
        );
      })
      .addCase(updateRiskLevel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteSeverityLevel.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteSeverityLevel.fulfilled, (state, action) => {
        state.loading = false;
        state.severityLevels = state.severityLevels.filter((severityLevel) => severityLevel.id !== action.payload.id);
      })
      .addCase(deleteSeverityLevel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteLikelihoodLevel.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteLikelihoodLevel.fulfilled, (state, action) => {
        state.loading = false;
        state.likelihoodLevels = state.likelihoodLevels.filter(
          (likelihoodLevel) => likelihoodLevel.id !== action.payload.id
        );
      })
      .addCase(deleteLikelihoodLevel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteRiskLevel.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteRiskLevel.fulfilled, (state, action) => {
        state.loading = false;
        state.riskLevels = state.riskLevels.filter((riskLevel) => riskLevel.id !== action.payload.id);
      })
      .addCase(deleteRiskLevel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});
